import { useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const fetchLeaveTypeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/lms/leave-types');
  const types = res.data.leaveTypes || [];
  const filtered = search ? types.filter((t: any) => t.name.toLowerCase().includes(search.toLowerCase())) : types;
  return filtered.map((t: any) => ({ value: t.id, label: `${t.name} (${t.code})` }));
};

export default function LeaveRecalculate() {
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [employeeNames, setEmployeeNames] = useState<Record<string, string>>({});
  const [leaveTypeIds, setLeaveTypeIds] = useState<string[]>([]);
  const [leaveTypeNames, setLeaveTypeNames] = useState<Record<string, string>>({});
  const [financialYear, setFinancialYear] = useState(String(new Date().getFullYear()));
  const [updateEligible, setUpdateEligible] = useState(false);
  const [createCycleIfNotFound, setCreateCycleIfNotFound] = useState(true);
  const [result, setResult] = useState<number | null>(null);

  const run = async () => {
    if (!employeeIds.length || !leaveTypeIds.length) return alert('Select at least one employee and one leave type');
    try {
      const res = await api.post('/lms/leave-recalculate', { employeeIds, leaveTypeIds, financialYear, updateEligible, createCycleIfNotFound });
      setResult(res.data.updated);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to recalculate');
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Leave Recalculate</h1>
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Employees</label>
          <LookupSelect
            value=""
            placeholder="Add employee..."
            fetchOptions={fetchEmployeeOptions}
            onChange={(v, opt) => {
              if (!v || employeeIds.includes(v)) return;
              setEmployeeIds([...employeeIds, v]);
              if (opt) setEmployeeNames({ ...employeeNames, [v]: opt.label });
            }}
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {employeeIds.map((id) => (
              <span key={id} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                {employeeNames[id] || id}
                <button onClick={() => setEmployeeIds(employeeIds.filter((e) => e !== id))} title="Remove">&times;</button>
              </span>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Leave Types (AL / ML / CCL / OIL)</label>
          <LookupSelect
            value=""
            placeholder="Add leave type..."
            fetchOptions={fetchLeaveTypeOptions}
            onChange={(v, opt) => {
              if (!v || leaveTypeIds.includes(v)) return;
              setLeaveTypeIds([...leaveTypeIds, v]);
              if (opt) setLeaveTypeNames({ ...leaveTypeNames, [v]: opt.label });
            }}
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {leaveTypeIds.map((id) => (
              <span key={id} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                {leaveTypeNames[id] || id}
                <button onClick={() => setLeaveTypeIds(leaveTypeIds.filter((e) => e !== id))} title="Remove">&times;</button>
              </span>
            ))}
          </div>
        </div>
        <Input label="Financial Year" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={updateEligible} onChange={(e) => setUpdateEligible(e.target.checked)} />
          Update-Eligible (recompute eligibility + balances; unchecked = balances only)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={createCycleIfNotFound} onChange={(e) => setCreateCycleIfNotFound(e.target.checked)} />
          Create Leave Cycle if Not Found
        </label>
        <Button onClick={run}>Recalculate</Button>
        {result !== null && <p className="text-sm text-green-700">Updated {result} balance record(s).</p>}
      </div>
    </div>
  );
}
